import React, {useState, useEffect, useRef} from "react";
import { Typography, Paper, Grid, LinearProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
    BufferGeometry,
    Float32BufferAttribute,
    PerspectiveCamera,
    Points,
    PointsMaterial,
    Scene,
    WebGLRenderer
} from "three";
import { getPointCloudData } from "src/containers/ImageViewer/PointCloudDataRetriever";
import { PCD_HARDWARE_SOURCE_LIST } from "src/containers/ImageViewer/constant";

const useStyles = makeStyles(({
    imageItem: {
        textAlign: "center",
    },
    sensorText : {
        paddingTop: 10,
        marginBottom: 10
    },
    imgContainer: {
        position: "relative"
    },
    image: {
        width: "calc(100% - 20px)",
        height: "calc((100vw - 20px) * 0.25)",
        paddingBottom: 20
    },
    imgHidden: {
        display: "none"
    },
    imgVisible: {
        display: "block"
    },
    progressBar: {
        "& .MuiLinearProgress-barColorPrimary": {
            backgroundColor: "#3474b4"
        }
    },
    errorMessageText: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignContent: "center",
        color: "red",
        width: "calc(100% - 20px)",
        height: "calc((100vw - 20px) * 0.25)"
    }
}));

type PointCloudImageViewerItemProps = {
    hardwareSource: string,
    sensor: string,
    imgUrl: string
}

/**
 * The Component is used to display point cloud images by extracting point cloud data based on hardware source
 */
const PointCloudImageViewerItem:
    React.FunctionComponent<PointCloudImageViewerItemProps> = (props: PointCloudImageViewerItemProps) => {

    const styles = useStyles();
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const [badUrl, setBadUrl] = useState<boolean>(false);
    const [renderingError, setRenderingError] = useState<boolean>(false);

    const FILE_RETRIEVE_ERROR_MESSAGE: string = 'Error occurred while retrieving file';
    const FILE_RENDER_ERROR_MESSAGE: string = 'Error occurred while rendering file';

    // Declaring these as component variables instead of state variables using hooks because when declared
    // as state variables the values are not being updated as expected when updated using callback functions
    // due to asynchronous nature of React hooks
    let canvas = useRef<HTMLElement>();
    let width = useRef<number>(500);
    let height = useRef<number>(500);
    let fieldOfView = useRef<number>(75);
    let aspectRatio = useRef<number>(width.current / height.current);
    let nearPlane = useRef<number>(-50);
    let farPlane = useRef<number>(20);
    let cameraY = useRef<number>(5);
    let cameraZ = useRef<number>(100);
    let targetRotationX = useRef<number>(0);
    let targetRotationOnMouseDownX = useRef<number>(0);
    let targetRotationY = useRef<number>(0);
    let targetRotationOnMouseDownY = useRef<number>(0);
    let mouseX = useRef<number>(0);
    let mouseXOnMouseDown = useRef<number>(0);
    let mouseY = useRef<number>(0);
    let mouseYOnMouseDown = useRef<number>(0);
    let finalRotationY = useRef<number>(0);

    let camera = useRef<PerspectiveCamera>();
    let scene = useRef<Scene>();
    let geometry = useRef<BufferGeometry>();
    let material = useRef<PointsMaterial>();
    let points = useRef<Points>();
    let renderer = useRef<WebGLRenderer>();
    let vertices = useRef<any[]>();

    const resetSceneValues = () => {
        fieldOfView.current = 75;
        aspectRatio.current = width.current / height.current;
        nearPlane.current = -50;
        farPlane.current = 20;
        cameraY.current = 5;
        cameraZ.current = 100;
        targetRotationX.current = 0;
        targetRotationOnMouseDownX.current = 0;
        targetRotationY.current = 0;
        targetRotationOnMouseDownY.current = 0;
        mouseX.current = 0;
        mouseXOnMouseDown.current = 0;
        mouseY.current = 0;
        mouseYOnMouseDown.current = 0;
    };
    // Create a scene for the point cloud points using the vertices of each point
    const initScene = () => {
        resetSceneValues();
        camera.current = new PerspectiveCamera(
            fieldOfView.current, aspectRatio.current, nearPlane.current, farPlane.current);
        camera.current.position.y = cameraY.current;
        camera.current.position.z = cameraZ.current;

        geometry.current = new BufferGeometry();
        geometry.current.setAttribute('position', new Float32BufferAttribute(vertices.current!, 3));
        material.current = new PointsMaterial({color: 0x888888});
        points.current = new Points(geometry.current, material.current);
        renderer.current = new WebGLRenderer({canvas: canvas.current});

        scene.current = new Scene();
        scene.current.add(points.current);
    };
    // Create an animation frame for the scene and render the scene with points
    const animate = () => {
        requestAnimationFrame(animate);
        animateRenderer();
    };
    const animateRenderer = () => {
        // horizontal rotation
        points.current!.rotation.y += (targetRotationX.current - points.current!.rotation.y) * 0.1;

        // vertical rotation
        finalRotationY.current = targetRotationY.current - points.current!.rotation.x;
        points.current!.rotation.x += finalRotationY.current * 0.1;

        renderer.current!.render(scene.current!, camera.current!);
    };
    // Event listeners to handle mouse movement and frame rotations
    const onDocumentMouseDown = (event: any) => {
        event.preventDefault();

        canvas.current!.addEventListener('mousemove', onDocumentMouseMove, false);
        canvas.current!.addEventListener('mouseup', onDocumentMouseUp, false);
        canvas.current!.addEventListener('mouseout', onDocumentMouseOut, false);

        mouseXOnMouseDown.current = event.clientX;
        targetRotationOnMouseDownX.current = targetRotationX.current;

        mouseYOnMouseDown.current = event.clientY;
        targetRotationOnMouseDownY.current = targetRotationY.current;
    };
    const onDocumentMouseMove = (event: any) => {
        mouseX.current = event.clientX;
        mouseY.current = event.clientY;

        targetRotationX.current =
            targetRotationOnMouseDownX.current + (mouseX.current - mouseXOnMouseDown.current) * 0.02;
        targetRotationY.current =
            targetRotationOnMouseDownY.current + (mouseY.current - mouseYOnMouseDown.current) * 0.02;
    };
    const onDocumentMouseUp = (event: any) => {
        canvas.current!.removeEventListener('mousemove', onDocumentMouseMove, false);
        canvas.current!.removeEventListener('mouseup', onDocumentMouseUp, false);
        canvas.current!.removeEventListener('mouseout', onDocumentMouseOut, false);
    };
    const onDocumentMouseOut = () => {
        canvas.current!.removeEventListener('mousemove', onDocumentMouseMove, false);
        canvas.current!.removeEventListener('mouseup', onDocumentMouseUp, false);
        canvas.current!.removeEventListener('mouseout', onDocumentMouseOut, false);
    };

    // Data retriever will invoke this callback once the vertices of points are extracted from file
    const setVerticesCallback = (pointVertices: any[]) => {
        vertices.current = pointVertices;
        initScene();
        animate();
        setImageLoaded(true);
    };

    // Initialize the canvas to render the animation and add event listeners
    // useEffect with empty dependencies will simulate the behavior of componentDidMount function
    useEffect(() => {
        canvas.current = document.getElementById('pointCloudViewer')!;
        if (canvas.current)
            canvas.current.addEventListener('mousedown', onDocumentMouseDown, false);
    }, []);

    // Retrieve point cloud data whenever there is a change in component props
    useEffect(() => {
        setImageLoaded(false);
        if (props.imgUrl) {
            if (PCD_HARDWARE_SOURCE_LIST.includes(props.hardwareSource)) {
                getPointCloudData(props.imgUrl, setBadUrl, setRenderingError, setVerticesCallback);
            }
        }
        else {
            setBadUrl(true);
        }
    }, [props.imgUrl]);

    const generateContentToDisplay = () => {
        if (badUrl)
            return (<div id="errorMessage" className={styles.errorMessageText}>{FILE_RETRIEVE_ERROR_MESSAGE}</div>);
        else if (renderingError)
            return (<div id="errorMessage" className={styles.errorMessageText}>{FILE_RENDER_ERROR_MESSAGE}</div>);
        else
            return (<canvas id="pointCloudViewer"
                            className={styles.image}
                            width={width.current}
                            height={height.current}></canvas>);
    };

    return(
        <Grid className={styles.imageItem} item xs={4}>
            <Paper elevation={2}>
                <Grid item xs={12}>
                    <Typography
                        className={styles.sensorText}
                        component='p'>
                        {props.sensor.toUpperCase()}
                    </Typography>
                </Grid>
                <Grid className={styles.imgContainer} item xs={12}>
                    {imageLoaded ? <div></div> : <LinearProgress className={styles.progressBar}/>}
                    <div className={imageLoaded ? styles.imgVisible : styles.imgHidden}>
                        {generateContentToDisplay()}
                    </div>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default PointCloudImageViewerItem;